// Moderation queue: all content reports, newest first, filterable by status.
// The page is gated on the JWT role claim for UX only — RLS already limits
// non-admins to their own reports, so nothing here grants access.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { ShieldAlert } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  ADMIN_CONTENT_REPORTS,
  ADMIN_PERSON_STATUSES,
  ADMIN_SET_PERSON_STATUS,
  UPDATE_CONTENT_REPORT_STATUS,
  type AdminContentReportsResult,
  type AdminPersonStatusesResult,
  type ContentReportRow,
  type PersonStatus,
  type ReportStatus,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import { formatRelativeTime } from "../lib/format";
import { useT } from "../lib/i18n";
import { Chip, EmptyState, ErrorBox, Spinner } from "../components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STATUS_TABS: (ReportStatus | "ALL")[] = ["OPEN", "RESOLVED", "DISMISSED", "ALL"];

/** COMMENT is absent on purpose — comments have no page of their own. */
const ENTITY_PATHS: Partial<Record<ContentReportRow["entityKind"], string>> = {
  PLACE: "/place",
  REGION: "/region",
  ROUTE: "/route",
  OUTING: "/outing",
  PERSON: "/person",
};

const STATUS_PILL: Record<ReportStatus, string> = {
  OPEN: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  RESOLVED: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  DISMISSED: "bg-secondary text-secondary-foreground",
};

const PERSON_STATUS_PILL: Record<PersonStatus, string> = {
  ACTIVE: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  SUSPENDED: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  BANNED: "bg-destructive/15 text-destructive dark:bg-destructive/25",
  DELETED: "bg-secondary text-secondary-foreground",
};

export function AdminReportsPage() {
  const { isAdmin } = useAuth();
  const { t, labelizeT } = useT();
  const [tab, setTab] = useState<ReportStatus | "ALL">("OPEN");
  const queryClient = useQueryClient();

  const reports = useQuery({
    queryKey: ["adminReports", tab],
    enabled: isAdmin,
    queryFn: () =>
      gqlRequest<AdminContentReportsResult>(ADMIN_CONTENT_REPORTS, {
        filter: tab === "ALL" ? null : { status: { equalTo: tab } },
      }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["adminReports"] });
    void queryClient.invalidateQueries({ queryKey: ["openReportCount"] });
  };

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ReportStatus }) =>
      gqlRequest(UPDATE_CONTENT_REPORT_STATUS, { id, status }),
    onSuccess: invalidate,
  });

  const setPersonStatus = useMutation({
    mutationFn: ({ p, s }: { p: string; s: PersonStatus }) => gqlRequest(ADMIN_SET_PERSON_STATUS, { p, s }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["adminPersonStatuses"] }),
  });

  // Current account status for every reported person on screen, in one
  // batch. person.status is grant-limited to gaea_admin (selecting it as
  // anyone else fails the whole request), so this document only ever runs
  // behind the isAdmin gate and never rides along in a shared person query.
  const reportedPersonIds = [
    ...new Set(
      (reports.data?.contentReports.nodes ?? [])
        .filter((r) => r.entityKind === "PERSON")
        .map((r) => r.entityId),
    ),
  ].sort();
  const personStatuses = useQuery({
    queryKey: ["adminPersonStatuses", reportedPersonIds],
    enabled: isAdmin && reportedPersonIds.length > 0,
    queryFn: () => gqlRequest<AdminPersonStatusesResult>(ADMIN_PERSON_STATUSES, { ids: reportedPersonIds }),
  });
  const statusById = new Map(personStatuses.data?.people.nodes.map((p) => [p.id, p.status]) ?? []);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <EmptyState>
          <ShieldAlert aria-hidden className="mx-auto mb-2 size-6 text-muted-foreground" />
          {t("admin.adminsOnly")}
        </EmptyState>
      </div>
    );
  }

  const nodes = reports.data?.contentReports.nodes ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">{t("admin.title")}</h1>
        {reports.data && (
          <span className="text-sm text-muted-foreground">
            {t(reports.data.contentReports.totalCount === 1 ? "admin.reportsOne" : "admin.reportsOther", {
              count: reports.data.contentReports.totalCount,
            })}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((s) => (
          <Chip key={s} active={tab === s} onClick={() => setTab(s)}>
            {s === "ALL" ? t("admin.all") : labelizeT(s)}
          </Chip>
        ))}
      </div>

      {(setStatus.error != null || setPersonStatus.error != null) && (
        <ErrorBox error={setStatus.error ?? setPersonStatus.error} />
      )}
      {setPersonStatus.isSuccess && (
        <p className="text-sm text-emerald-700 dark:text-emerald-400">{t("admin.accountUpdated")}</p>
      )}

      {reports.isPending ? (
        <Spinner label={t("admin.loadingReports")} />
      ) : reports.error != null ? (
        <ErrorBox error={reports.error} />
      ) : nodes.length === 0 ? (
        <EmptyState>{t("admin.noReports")}</EmptyState>
      ) : (
        <ul className="divide-y divide-border rounded-xl border bg-card text-card-foreground shadow-card">
          {nodes.map((report) => (
            <ReportRow
              key={report.id}
              report={report}
              personStatus={report.entityKind === "PERSON" ? (statusById.get(report.entityId) ?? null) : null}
              busy={setStatus.isPending || setPersonStatus.isPending}
              onSetStatus={(status) => setStatus.mutate({ id: report.id, status })}
              onSetPersonStatus={(s) => setPersonStatus.mutate({ p: report.entityId, s })}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ReportRow({
  report,
  personStatus,
  busy,
  onSetStatus,
  onSetPersonStatus,
}: {
  report: ContentReportRow;
  /** Reported person's account status (PERSON reports only; null while loading). */
  personStatus: PersonStatus | null;
  busy: boolean;
  onSetStatus: (status: ReportStatus) => void;
  onSetPersonStatus: (s: PersonStatus) => void;
}) {
  const { t, labelizeT } = useT();
  const path = ENTITY_PATHS[report.entityKind];

  return (
    <li className="space-y-2 p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge variant="secondary" className="uppercase tracking-wide">
          {labelizeT(report.entityKind)}
        </Badge>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_PILL[report.status]}`}>
          {labelizeT(report.status)}
        </span>
        {path ? (
          <Link
            href={`${path}/${report.entityId}`}
            className="font-medium text-primary hover:underline"
          >
            {t("admin.view", { kind: labelizeT(report.entityKind) })}
          </Link>
        ) : (
          <span className="font-mono text-xs text-muted-foreground">{report.entityId}</span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">{formatRelativeTime(report.createdAt)}</span>
      </div>

      <p className="text-sm text-foreground">{report.body}</p>

      <p className="text-xs text-muted-foreground">
        {t("admin.reportedBy")}{" "}
        <Link href={`/person/${report.reporterId}`} className="text-primary hover:underline">
          {report.reporter?.displayName ?? report.reporterId}
        </Link>
      </p>

      <div className="flex flex-wrap gap-2 pt-1">
        {report.status === "OPEN" && (
          <>
            <Button type="button" disabled={busy} onClick={() => onSetStatus("RESOLVED")} variant="outline" size="sm">
              {t("admin.resolve")}
            </Button>
            <Button type="button" disabled={busy} onClick={() => onSetStatus("DISMISSED")} variant="outline" size="sm">
              {t("admin.dismiss")}
            </Button>
          </>
        )}
        {report.entityKind === "PERSON" && (
          <>
            {personStatus != null && (
              <span
                className={`self-center rounded-full px-2 py-0.5 text-xs font-semibold ${PERSON_STATUS_PILL[personStatus]}`}
              >
                {t("admin.account", { status: labelizeT(personStatus) })}
              </span>
            )}
            {/* Only the transition that applies: ACTIVE can be suspended,
                SUSPENDED reinstated; BANNED/DELETED show the chip alone. */}
            {personStatus === "ACTIVE" && (
              <Button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(t("admin.confirmSuspend"))) {
                    onSetPersonStatus("SUSPENDED");
                  }
                }}
                variant="outline"
                size="sm"
                className="text-destructive"
              >
                {t("admin.suspend")}
              </Button>
            )}
            {personStatus === "SUSPENDED" && (
              <Button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(t("admin.confirmReinstate"))) {
                    onSetPersonStatus("ACTIVE");
                  }
                }}
                variant="outline"
                size="sm"
              >
                {t("admin.reinstate")}
              </Button>
            )}
          </>
        )}
      </div>
    </li>
  );
}
